import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatTabGroup } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { defer, firstValueFrom, from, merge, Observable, of, Subscription } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import * as showdown from 'showdown';
import jsPDF from 'jspdf';
import * as docx from 'docx';
import { saveAs } from 'file-saver';

import { AIFireService, AnswerLength } from '../../gen2ai/fire.service';
import { Message } from '../../models/chat.model';
import { SideMenusService } from '../../core/side-menus/side-menus.service';
import { ReasoningService, ReasoningLevel } from 'src/app/services/reasoning.service';

import { TeamAiDragDropDialogComponent } from '../../components/dialogs/team-ai-drag-drop-dialog/team-ai-drag-drop-dialog.component';
import {
  getClariChatModelsFlat,
  getClariChatModelsForTeamComparePickers,
  CLARI_CHAT_MODEL_VENDOR_BY_ID
} from '../../shared/clari-chat-model-catalog';
import { TheVoiceComponent } from '../../authentication/dialogs/the-voice/the-voice.component';
import { ClariCoachDialogComponent } from '../../authentication/dialogs/clari-coach-dialog/clari-coach-dialog.component';
import { GoogleAudioService, LiveTranscriptChunk } from '../../services/googleaudio.service';
import { TourService, TourStep } from '../../authentication/tour.service';
import { ClarityCoachService } from '../../services/clarity-coach.service';

import { GptFiveComponent } from '../../authentication/dialogs/gpt-five/gpt-five.component';
import { GeminiProComponent } from '../../authentication/dialogs/gemini-pro/gemini-pro.component';
import { DeepseekComponent } from '../../authentication/dialogs/deepseek/deepseek.component';
import { Deepseekv3Component } from '../../authentication/dialogs/deepseekv3/deepseekv3.component';
import { OpusFourComponent } from '../../authentication/dialogs/opus-four/opus-four.component';
import { HaikuComponent } from '../../authentication/dialogs/haiku/haiku.component';
import { SonnetFourComponent } from '../../authentication/dialogs/sonnet-four/sonnet-four.component';
import { PixtralImageComponent } from '../../authentication/dialogs/pixtral-image/pixtral-image.component';
import { MistralLargeComponent } from '../../authentication/dialogs/mistral-large/mistral-large.component';
import { MaverickComponent } from '../../authentication/dialogs/maverick/maverick.component';
import { JambaComponent } from '../../authentication/dialogs/jamba/jamba.component';
import { GlmComponent } from '../../authentication/dialogs/glm/glm.component';
import { PalmyraComponent } from '../../authentication/dialogs/palmyra/palmyra.component';
import { CohereRPlusComponent } from '../../authentication/dialogs/cohere-r-plus/cohere-r-plus.component';
import { NovaProComponent } from '../../authentication/dialogs/nova-pro/nova-pro.component';
import { ScoutComponent } from '../../authentication/dialogs/scout/scout.component';
import { OpusSixComponent } from '../../authentication/dialogs/opus-six/opus-six.component';
import { aggressivelyFormatMarkdownForCodeBlocks } from '../../shared/markdown/aggressive-code-format';


/** Team tab model id (aligned with Clari Chat catalog + legacy multichat ids). */
type ModelId = string;

interface ModelMeta {
  id: string;
  name: string;
}

interface PlaceholderOption {
  text: string;
  logoUrl: string;
  name: string;
}

type TabStatus = 'idle' | 'success' | 'error';

@Component({
  selector: 'app-multichat',
  templateUrl: './multichat.component.html',
  styleUrls: ['./multichat.component.scss']
})
export class MultichatComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly FIRST_VISIT_TOUR_KEY = 'executiveChatFirstVisitTourShown';
  private firstVisitTourTimeout?: ReturnType<typeof setTimeout>;

  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  @ViewChild('tabGroup', { read: ElementRef }) tabGroupElement!: ElementRef<HTMLElement>;
  @ViewChild('menuContainer') menuContainer!: ElementRef;
  @ViewChild('userInputTextarea', { static: false }) textareaRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('videoMenuTrigger', { static: false }) videoMenuTrigger!: ElementRef<HTMLElement>;
  @ViewChild('videoMenu', { static: false }) videoMenuRef!: ElementRef<HTMLElement>;

  // ✅ Default to summary tab (first tab)
  selectedTabIndex = 0;

  menuClose = false;

  form: FormGroup;

  inputText = '';
  isLoading = false;
  error: string | null = null;
  audioActive = false;
  showLogo = true;
  logoKey = 0;

  //textarea placeholder magic
  isFocused = false;
  onTextareaFocus(): void {
  this.isFocused = true;
}
  onTextareaBlur(): void {
  this.isFocused = false;
}

  private readonly executivePlaceholderFull =
    '4 Comparative Answers - Executive Clarity';
  private readonly executivePlaceholderNarrow = '4 Comparative Answers';
  executiveComposerPlaceholder = this.executivePlaceholderFull;
  private narrowViewportMql?: MediaQueryList;
  private readonly onNarrowViewportPlaceholderChange = (e: MediaQueryListEvent): void => {
    this.ngZone.run(() => {
      this.executiveComposerPlaceholder = e.matches
        ? this.executivePlaceholderNarrow
        : this.executivePlaceholderFull;
      this.cdr.markForCheck();
    });
  };

  currentMessages: Message[] = [];
  coachFabBusy = false;
  coachFabText = 'Coach';

  // optional “answer length” for calls (defaults to normal)
  answerLength: AnswerLength = 'normal';

  readonly MODEL_CATALOG: Record<string, string> = {
    ...Object.fromEntries(getClariChatModelsFlat().map((m) => [m.id, m.name])),
    qwen: 'Qwen 3 Coder',
    glm5: 'GLM 5',
    glm47: 'GLM-4.7',
    gemini3flash: 'Gemini Flash 3',
    haiku: 'Claude Haiku 4.5'
  };

  readonly MODEL_VENDOR_MAP: Record<string, string> = {
    ...CLARI_CHAT_MODEL_VENDOR_BY_ID,
    qwen: 'qwen',
    glm5: 'zai',
    glm47: 'zai',
    gemini3flash: 'google',
    haiku: 'anthropic'
  };

  selectedModels: ModelId[] = ['mistrallarge', 'palmx', 'chatgpt54', 'haiku45'];

  rawOutputs: (string | null)[] = this.createEmptyRawOutputs();
  formattedOutputs: SafeHtml[] = this.createEmptyFormattedOutputs();

  teamSummary: string | null = null;
  summaryRelevance: string | null = null;
  summaryFurtherAnalysis: string | null = null;
  formattedSummary: SafeHtml = '';
  summarySucceeded = false;
  summaryError: string | null = null;
  liveTranscriptHtml: SafeHtml = '';
  liveTranscriptActive = false;
  private liveTranscriptSegments: Array<{ role: 'user' | 'model'; text: string }> = [];
  private liveTranscriptPartial: { role: 'user' | 'model'; text: string } | null = null;

  // ✅ NEW: status icons on tabs
  tabStatuses: TabStatus[] = this.createIdleTabStatuses();
  private readonly maxRetries = 3;

  private md = new showdown.Converter({
    tables: true,
    simplifiedAutoLink: true,
    strikethrough: true,
    tasklists: true
  });

  private prismLoaded = false;
  private prism: any = null;

  private hljsLoaded = false;
  private hljs: any = null;

private typingTimer: any = null;
private headerTypingIndex = 0;     // which placeholder option
private headerCharIndex = 0;       // which character inside the option text
private headerDeleting = false;    // typing forward vs deleting


  showVideoGeneratorMenu = false;
  messageHistory: string[] = [];
  historyIndex = -1;

  private focusTextarea(): void {
    setTimeout(() => this.textareaRef?.nativeElement?.focus(), 0);
  }

  onTabIndexChange(index: number): void {
    this.selectedTabIndex = index;
    this.scrollActiveTabIntoView(index);
  }

  private scrollActiveTabIntoView(index: number): void {
    const host = this.tabGroupElement?.nativeElement;
    if (!host) return;

    const labels = host.querySelectorAll<HTMLElement>('.mat-tab-label, .mat-mdc-tab');
    const target = labels?.[index];
    if (!target) return;

    requestAnimationFrame(() => {
      const container = target.closest<HTMLElement>('.mat-tab-label-container, .mat-mdc-tab-label-container');
      if (!container) return;

      const targetRect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (targetRect.left < containerRect.left) {
        container.scrollTo({ left: container.scrollLeft + targetRect.left - containerRect.left, behavior: 'smooth' });
      } else if (targetRect.right > containerRect.right) {
        container.scrollTo({ left: container.scrollLeft + targetRect.right - containerRect.right, behavior: 'smooth' });
      }
    });
  }

  private composingAtTop = false;
  private shouldAutoFollowAnswers = true;
  private answerScrollHost?: HTMLElement;

  private readonly onAnswerScroll = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches('.mat-tab-body-content, .mat-mdc-tab-body-content')) {
      return;
    }

    this.shouldAutoFollowAnswers = this.isNearBottom(target);
  };

  // Track active request subscription so we can cancel on new submit
  private requestsSub?: Subscription;

  // ======================================================================
  // Video menu positioning (unchanged)
  // ======================================================================
  private positionVideoGeneratorMenu(): void {
    const trigger = this.videoMenuTrigger?.nativeElement;
    const menu = this.videoMenuRef?.nativeElement;
    if (!trigger || !menu) return;

    const padding = 8;

    const prevVisibility = menu.style.visibility;
    const prevDisplay = menu.style.display;

    menu.style.visibility = 'hidden';
    menu.style.display = 'block';

    const menuRect = menu.getBoundingClientRect();
    const t = trigger.getBoundingClientRect();

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const space = {
      top: t.top - padding,
      bottom: vh - t.bottom - padding,
      left: t.left - padding,
      right: vw - t.right - padding
    };

    const fits = {
      top: space.top >= menuRect.height,
      bottom: space.bottom >= menuRect.height,
      right: space.right >= menuRect.width,
      left: space.left >= menuRect.width
    };

    type Placement = 'top' | 'bottom' | 'right' | 'left';
    const placement: Placement =
      fits.top ? 'top' :
      fits.bottom ? 'bottom' :
      fits.right ? 'right' :
      fits.left ? 'left' :
      (Object.entries(space).sort((a, b) => (b[1] as number) - (a[1] as number))[0][0] as Placement);

    let top = 0;
    let left = 0;
    const gap = 10;

    if (placement === 'top') {
      top = t.top - menuRect.height - gap;
      left = t.right - menuRect.width;
      menu.style.transformOrigin = 'bottom right';
    } else if (placement === 'bottom') {
      top = t.bottom + gap;
      left = t.right - menuRect.width;
      menu.style.transformOrigin = 'top right';
    } else if (placement === 'right') {
      top = t.top + (t.height / 2) - (menuRect.height / 2);
      left = t.right + gap;
      menu.style.transformOrigin = 'center left';
    } else {
      top = t.top + (t.height / 2) - (menuRect.height / 2);
      left = t.left - menuRect.width - gap;
      menu.style.transformOrigin = 'center right';
    }

    top = Math.max(padding, Math.min(top, vh - menuRect.height - padding));
    left = Math.max(padding, Math.min(left, vw - menuRect.width - padding));

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.right = 'auto';
    menu.style.bottom = 'auto';
    menu.style.maxWidth = `calc(100vw - ${padding * 2}px)`;

    menu.style.visibility = prevVisibility;
    menu.style.display = prevDisplay;
  }

  // ======================================================================

  onEnter(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isLoading || !this.inputText.trim()) return;
    this.onSubmit();
    setTimeout(() => this.autoResizeTextarea(), 0);
  }

  onShiftEnter(event: KeyboardEvent, el: HTMLTextAreaElement): void {
    event.preventDefault();
    event.stopPropagation();
    (event as any).stopImmediatePropagation?.();

    this.composingAtTop = true;

    const prevScrollTop = el.scrollTop;
    const start = el.selectionStart ?? this.inputText.length;
    const end = el.selectionEnd ?? this.inputText.length;

    this.inputText = this.inputText.slice(0, start) + '\n' + this.inputText.slice(end);

    const nextPos = start + 1;

    setTimeout(() => {
      el.focus();
      el.setSelectionRange(nextPos, nextPos);
      this.autoResizeTextarea(prevScrollTop);
    });
  }

  // ---------------- Rotating header placeholders ----------------
  placeholderOptions: PlaceholderOption[] = [
    { text: 'Democratizing ArtificialIntelligence', logoUrl: 'assets/svg/sosexy.png', name: 'ClariChat' },
    { text: 'Vision & Maverick - Top Gun AI', logoUrl: 'assets/logos/meta.svg', name: 'Meta AI' },
    { text: 'Flagship reasoning and coding performance', logoUrl: 'assets/logos/openai.svg', name: 'ChatGPT 5.4' },
    { text: 'Best Model For Code, Ever', logoUrl: 'assets/logos/anthropic.svg', name: 'Opus 4.7' },
    { text: 'Balance Performance & Cost = X5', logoUrl: 'assets/logos/x5-baby.png', name: 'Writer X5' },
    { text: 'The Open Source Rocky Balboa', logoUrl: 'assets/logos/deepseek.svg', name: 'DeepSeek R1' },
    { text: 'ChatGPT Meets Pro Competition', logoUrl: 'assets/logos/google.svg', name: 'Gemini Pro 3' },
    { text: 'Super Live - Super Search', logoUrl: 'assets/logos/xai.svg', name: 'Grok 4' },
    { text: 'Advanced Language ? Understanding Enterprise', logoUrl: 'assets/logos/cohere.svg', name: 'Cohere R+' },

  ];

  currentOption: PlaceholderOption = this.placeholderOptions[0];
  logoFading = false;
  modelNameFading = false;

  readonly modelTabLabelColor = '#800042';

  showMainSubtitle = true;
  currentPlaceholder = '';
  private placeholderIndex = 0;

  // shimmer
  isShimmering = false;
  private shimmerDirection: 'teal-to-pink' | 'pink-to-teal' = 'teal-to-pink';
  private shimmerLetterIndex = -1;
  private shimmerLetters: string[] = [];

// Typewriter timings
private readonly typeSpeedMs = 120;          // typing forward speed
private readonly backspaceSpeedMs = 100;     // deleting speed (usually faster)
private readonly pauseAfterTypeMs = 1500;    // pause at full text
private readonly pauseAfterDeleteMs = 500;  // pause when cleared before next phrase
private readonly shimmerStepMs = 70;        // shimmer still ok to keep

//TEMP COMPAT: keep these so existing code still compiles
private readonly placeholderHoldMs = 2400;
private readonly fadeDurationMs = 250;
private readonly modelPlaceholderHoldMs = 2600;


  private placeholderTimer: any = null;
  private shimmerTimer: any = null;

  private destroyed = false;
  private cycleToken = 0;

  // ✅ FIX: stable per-letter identity
  trackByLetter = (_: number, item: { char: string; index: number }) => item.index;

  get modelKimColor(): string {
    return this.modelTabLabelColor;
  }

  get modelNameClass(): string {
    return this.isShimmering ? `model-name shimmer-${this.shimmerDirection}` : 'model-name';
  }

  get modelNameLetters(): Array<{ char: string; index: number }> {
    const name = this.currentOption?.name || 'AI Model';
    return Array.from(name).map((char, index) => ({ char, index }));
  }

  getLetterColor(letterIndex: number): string {
    if (!this.isShimmering) return this.modelKimColor;

    const startColor = '#008080';
    const endColor = '#800042';

    if (letterIndex < this.shimmerLetterIndex) return endColor;
    return startColor;
  }

  isLetterWaving(letterIndex: number): boolean {
    return this.isShimmering && letterIndex === this.shimmerLetterIndex;
  }

  // ---------------- model placeholders (tabs) ----------------
  modelPlaceholderOptions: Record<string, string[]> = {
    chatgpt54: ['Balanced reasoning for tough prompts', 'Confident answers with clarity'],
    palmx: ['Writer-grade tone with precision', 'Clear narrative with structure'],
    gemini3flash: ['Fast responses with steady quality', 'Speedy answers for live work'],
    deepseek: ['Math and code with rigor', 'Precise logic for hard problems'],
    deepv3: ['DeepSeek V3.2 — higher precision for deep tasks', 'Accurate reasoning with depth'],
    glm47: ['Strong code and analytics', 'Reliable outputs for builders'],
    qwen: ['Code-focused reasoning', 'Prompt + history Bedrock Qwen route'],
    glm5: ['Structured multilingual reasoning', 'Prompt + history GLM-5 route'],
    novapro: ['Enterprise-grade accuracy', 'Stable, structured answers'],
    maverick: ['Generalist strength', 'Steady problem solving'],
    opus37: ['Claude Opus 3.7', 'Balanced depth and speed'],
    opus45: ['Deep synthesis', 'Strong long-form output'],
    opus46: ['Bedrock Converse', 'Prompt + history + optional systemPrompt'],
    opus6: ['Bedrock Converse', 'Prompt + history + optional systemPrompt'],
    opus47: ['Opus 4.7 frontier', 'catalogModelId opus47 on Bedrock Converse'],
    kimik25: ['Kimi 2.5', 'Dedicated Lambda prompt + history'],
    haiku45: ['Fast and crisp', 'Concise helpful replies'],
    sonnet45: ['Creative + analytical.', 'Balanced reasoning'],
    cohere: ['Business clarity', 'Structured outputs'],
    commandLight: ['Fast enterprise chat', 'Lightweight Command Instruct responses'],
    gemini: ['Balanced and fast', 'Reliable responses'],
    jamba: ['Long context', 'Workflow-friendly'],
    mistrallarge: ['Technical depth', 'Multilingual strength'],
    pixtralimage: ['Vision-first', 'Image expert']
  };

  modelPlaceholders: Record<number, string> = {};
  modelPlaceholderIndices: Record<number, number> = {};
  modelPlaceholderTimers: Record<number, any> = {};

  clickedModels: Set<ModelId> = new Set<ModelId>();

  menuOpen = false;
  private menuStateSubscription?: Subscription;
  private transcriptSubscription?: Subscription;
  private transcriptActiveSubscription?: Subscription;
  private reasoningSubscription?: Subscription;
  selectedReasoningLevel: ReasoningLevel = 'thinking';

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private fire: AIFireService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private sideMenusService: SideMenusService,
    private googleAudio: GoogleAudioService,
    private reasoningService: ReasoningService,
    private coachService: ClarityCoachService,
    private tourService: TourService
  ) {
    this.form = this.fb.group({ inputText: [''] });
  }

  private selectSummaryTab(): void {
    const summaryIndex = 0;
    this.onTabIndexChange(summaryIndex);

    // tabGroup isn't available until view init
    if (this.tabGroup) this.tabGroup.selectedIndex = summaryIndex;

    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.scheduleFirstVisitTour();
    this.menuStateSubscription = this.sideMenusService.mainMenuOpen$.subscribe((open) => {
      this.menuOpen = open;
    });
    this.transcriptSubscription = this.googleAudio.transcript$.subscribe((chunk) => {
      this.applyLiveTranscriptChunk(chunk);
    });
    this.transcriptActiveSubscription = this.googleAudio.sessionActive$.subscribe((active) => {
      this.liveTranscriptActive = active;
      if (active && this.selectedTabIndex !== 0) {
        this.selectSummaryTab();
      }
      this.cdr.detectChanges();
    });
    this.selectedReasoningLevel = this.reasoningService.getReasoningLevel();
    this.reasoningSubscription = this.reasoningService.reasoningLevel$.subscribe((level) => {
      this.selectedReasoningLevel = level;
      this.cdr.detectChanges();
    });

    this.showMainSubtitle = false;
    this.cdr.detectChanges();
    this.startPerfectPlaceholderCycle();

    this.resetModelPlaceholderTimers();
    this.resetTabStatuses();

    this.setupExecutiveComposerPlaceholderMediaQuery();
  }

  async ngAfterViewInit(): Promise<void> {
    this.answerScrollHost = this.tabGroupElement?.nativeElement;
    this.answerScrollHost?.addEventListener('scroll', this.onAnswerScroll, true);
    await this.loadPrismIfNeeded();
    await this.loadHLJSIfNeeded();
    setTimeout(() => {
      this.highlightAll();
      this.highlightAllHLJS();
      this.autoResizeTextarea();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.firstVisitTourTimeout) {
      clearTimeout(this.firstVisitTourTimeout);
    }
    this.stopPerfectPlaceholderCycle();
    this.resetModelPlaceholderTimers(true);
    this.menuStateSubscription?.unsubscribe();
    this.requestsSub?.unsubscribe();
    this.transcriptSubscription?.unsubscribe();
    this.transcriptActiveSubscription?.unsubscribe();
    this.reasoningSubscription?.unsubscribe();
    this.narrowViewportMql?.removeEventListener('change', this.onNarrowViewportPlaceholderChange);
    this.answerScrollHost?.removeEventListener('scroll', this.onAnswerScroll, true);
  }

  private scheduleFirstVisitTour(attempt = 0): void {
    if (typeof window === 'undefined' || localStorage.getItem(this.FIRST_VISIT_TOUR_KEY)) {
      return;
    }

    const summaryTab = document.getElementById('executiveSummaryTab');
    const rect = summaryTab?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) {
      if (attempt < 20) {
        this.firstVisitTourTimeout = setTimeout(
          () => this.scheduleFirstVisitTour(attempt + 1),
          100
        );
      }
      return;
    }

    localStorage.setItem(this.FIRST_VISIT_TOUR_KEY, 'true');
    this.tourService.startTour(this.buildFirstVisitTour());
  }

  private buildFirstVisitTour(): TourStep[] {
    const summaryStep: TourStep = {
      elementId: 'executiveSummaryTab',
      title: 'Summary view',
      description: 'Review the consolidated comparison before diving into the details. This view highlights the overall answer quality and the key tradeoffs across models.',
      onStepAction: () => this.onTabIndexChange(0)
    };

    const aiSelectorStep: TourStep = {
      elementId: 'executiveAiSelector',
      title: 'AI selector',
      description: 'Use this selector at the bottom of the prompt area to choose which models participate in the comparison. Each model adds a different perspective, speed profile, and reasoning style.',
      onStepAction: () => this.onTabIndexChange(0)
    };

    const modelSteps = this.selectedModels.map((modelId, index): TourStep => ({
      elementId: `executiveModelTab${index + 1}`,
      title: `${this.modelName(modelId)} model view`,
      description: `This tab shows ${this.modelName(modelId)}'s answer in full. Use it to compare reasoning depth, output structure, and how each model handles the same request.`,
      onStepAction: () => this.onTabIndexChange(index + 1)
    }));

    return [
      summaryStep,
      aiSelectorStep,
      ...modelSteps,
      {
        elementId: 'aiCoachFab',
        title: 'AI Coach',
        description: 'The AI Coach reviews the latest successful exchange and explains why each model is stronger for a given task, what tradeoffs matter, and when a different model may produce a better outcome.',
        onStepAction: () => this.onTabIndexChange(0)
      },
      {
        elementId: 'executiveChatInput',
        title: 'Prompt input',
        description: 'Enter the question once and compare the outputs side by side. The system evaluates the selected models and then consolidates their responses.'
      }
    ];
  }

  private setupExecutiveComposerPlaceholderMediaQuery(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    this.narrowViewportMql = window.matchMedia('(max-width: 1024px)');
    this.executiveComposerPlaceholder = this.narrowViewportMql.matches
      ? this.executivePlaceholderNarrow
      : this.executivePlaceholderFull;
    this.narrowViewportMql.addEventListener('change', this.onNarrowViewportPlaceholderChange);
  }

  private normalizeCoachText(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.replace(/\s+/g, ' ').trim();
  }

  private getLatestCoachContext(): { userText: string; assistantText: string; statusCode: number; ok: boolean } | null {
    const recent = [...this.currentMessages].reverse();
    const userMessage = recent.find(message => message.role === 'user' && this.normalizeCoachText(message.content));
    const assistantMessage = recent.find(message => message.role === 'assistant' && this.normalizeCoachText(message.content));

    if (!userMessage || !assistantMessage) {
      return null;
    }

    const userText = this.normalizeCoachText(userMessage.content);
    const assistantText = this.normalizeCoachText(assistantMessage.content);
    if (!userText || !assistantText) {
      return null;
    }

    const statusCodeValue = Number(
      (assistantMessage as any).statusCode ??
      (assistantMessage as any).httpStatus ??
      (assistantMessage as any).responseStatus ??
      200
    );

    return {
      userText,
      assistantText,
      statusCode: Number.isFinite(statusCodeValue) ? statusCodeValue : 200,
      ok: true
    };
  }

  async triggerCoachFab(): Promise<void> {
    const context = this.getLatestCoachContext();
    if (!context) {
      console.warn('[Multichat] No chat exchange available for AI Coach.');
      return;
    }

    if (context.statusCode !== 200) {
      console.warn('[Multichat] AI Coach requires a successful response status.');
      return;
    }

    this.coachFabBusy = true;
    this.coachFabText = 'Working';

    try {
      const response = await this.coachService.requestDoubleCoachDeepSeek({
        ...context,
        preference: this.coachService.getPreference()
      });

      if (response?.status === 'ready' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('open-ai-coach-menu'));
        this.dialog.open(ClariCoachDialogComponent, {
          width: 'min(760px, 92vw)',
          maxWidth: '92vw',
          maxHeight: '80vh',
          panelClass: 'ai-coach-dialog-panel',
          data: {
            entries: this.coachService.getRecentCoachEntries(10)
          }
        });
      }

      this.coachFabText = response?.status === 'ready' ? 'Ready' : 'Coach';
    } catch (error) {
      this.coachFabText = 'Coach';
      console.error('[Multichat] AI Coach failed:', error);
    } finally {
      this.coachFabBusy = false;
    }
  }
  toggleMainMenu(): void {
    this.sideMenusService.toggleMainMenuSubject.next('toggle');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideVideoMenu = this.menuContainer?.nativeElement?.contains(target) ?? false;
    if (!clickedInsideVideoMenu) this.showVideoGeneratorMenu = false;
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.showVideoGeneratorMenu) this.positionVideoGeneratorMenu();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (this.showVideoGeneratorMenu) this.positionVideoGeneratorMenu();
  }

  toggleVideoGeneratorMenu(): void {
    this.showVideoGeneratorMenu = !this.showVideoGeneratorMenu;
    if (this.showVideoGeneratorMenu) {
      setTimeout(() => this.positionVideoGeneratorMenu(), 0);
      setTimeout(() => this.positionVideoGeneratorMenu(), 80);
    }
  }

  // ---------------- Open Drag & Drop dialog ----------------
  openModelDialog(): void {
    const available: ModelMeta[] = getClariChatModelsForTeamComparePickers().map((m) => ({
      id: m.id,
      name: m.name
    }));

    const dialogRef = this.dialog.open(TeamAiDragDropDialogComponent, {
      width: '1100px',
      maxWidth: '99vw',
      panelClass: 'team-ai-dnd-dialog',
      data: { selected: [...this.selectedModels], available }
    });

    dialogRef.afterClosed().subscribe((result: string[] | undefined) => {
      if (result && Array.isArray(result) && result.length === 4) {
        this.selectedModels = result as ModelId[];

        this.rawOutputs = this.createEmptyRawOutputs();
        this.formattedOutputs = this.createEmptyFormattedOutputs();

        this.teamSummary = null;
        this.summaryRelevance = null;
        this.summaryFurtherAnalysis = null;
        this.formattedSummary = '';

        this.resetModelPlaceholderTimers();
        this.resetTabStatuses();

        // reset to summary tab
        this.selectedTabIndex = 0;
        if (this.tabGroup) this.tabGroup.selectedIndex = 0;

        setTimeout(() => {
          this.highlightAll();
          this.highlightAllHLJS();
        }, 0);
      }
    });
  }

  // ===================== Voice entry point (DIALOG) =====================
  onAudioIconClick(): void {
    const ref = this.dialog.open(TheVoiceComponent, {
      width: '98vw',
      height: '98vh',
      maxWidth: '98vw',
      maxHeight: '98vh',
      panelClass: 'voice-dialog-panel',
      autoFocus: false,
      restoreFocus: true,
      hasBackdrop: true
    });

    ref.afterClosed().subscribe(() => (this.audioActive = false));
    this.audioActive = true;
  }

  handleSendOrAudioClick(): void {
    if (this.inputText?.trim()) {
      this.onSubmit();
      return;
    }
    this.onAudioIconClick();
  }

  handleTextareaKeydown(event: KeyboardEvent): void {
    const el = this.textareaRef?.nativeElement;
    if (!el) return;

    if (event.key === 'ArrowUp' && !event.shiftKey) {
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      if (start === 0 && end === 0) {
        event.preventDefault();
        this.recallPreviousMessage();
      }
      return;
    }

    if (event.key === 'ArrowDown' && !event.shiftKey) {
      const len = el.value?.length ?? 0;
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      if (start === len && end === len) {
        event.preventDefault();
        this.recallNextMessage();
      }
      return;
    }
  }

  private resetTextarea(): void {
    const el = this.textareaRef?.nativeElement;
    if (!el) return;
    el.style.height = '';
    el.style.overflowY = 'hidden';
    el.scrollTop = 0;
  }

  // ======================================================================
  // ✅ SUBMIT: Observable-first, per-model status, jump to first success
  // ======================================================================
  onSubmit(): void {
    const prompt = (this.inputText || '').trim();
    if (!prompt) return;

    const activeAnswers = this.getActiveAnswersScroller();
    this.shouldAutoFollowAnswers = !activeAnswers || this.isNearBottom(activeAnswers);

    // cancel any previous run
    this.requestsSub?.unsubscribe();
    this.requestsSub = undefined;

    // reset UI input
    this.inputText = '';
    setTimeout(() => this.autoResizeTextarea(), 0);
    this.resetTextarea();

    // update prompt history
    this.messageHistory.push(prompt);
    if (this.messageHistory.length > 10) this.messageHistory.shift();
    this.historyIndex = -1;

    // reset outputs + statuses
    this.error = null;
    this.isLoading = true;
    this.teamSummary = null;
    this.summaryRelevance = null;
    this.summaryFurtherAnalysis = null;
    this.formattedSummary = '';
    this.summarySucceeded = false;
    this.summaryError = null;
    this.rawOutputs = this.createEmptyRawOutputs();
    this.formattedOutputs = this.createEmptyFormattedOutputs();
    this.resetTabStatuses();

    // local history snapshot
    const history = [...this.currentMessages];

    let pending = this.selectedModels.length;
    let firstSuccessfulTabShown = false;

    const reqs = this.selectedModels.map((modelId, idx) =>
      this.invokeModel(modelId, prompt, history, this.answerLength).pipe(
        take(1),
        map((text) => ({
          idx,
          ok: Boolean(String(text || '').trim()),
          text: String(text ?? '')
        })),
        catchError((err) =>
          of({
            idx,
            ok: false,
            text: `(${this.MODEL_CATALOG[modelId]} error) ${this.errString(err)}`
          })
        )
      )
    );

    this.requestsSub = merge(...reqs).subscribe({
      next: (r) => {
        // store output
        this.rawOutputs[r.idx] = r.text || '';
        this.formattedOutputs[r.idx] = this.asHtml(r.text || '');

        // status icon
        this.tabStatuses[r.idx] = r.ok ? 'success' : 'error';

        // jump to the first successful model response
        if (!firstSuccessfulTabShown && r.ok) {
          firstSuccessfulTabShown = true;
          const tabIndex = r.idx + 1;
          this.selectedTabIndex = tabIndex;
          if (this.tabGroup) this.tabGroup.selectedIndex = tabIndex;
        }
        this.scheduleAnswerAutoFollow();

        pending -= 1;

        // once all are done, compute summary (but DO NOT navigate to it)
        if (pending <= 0) {
          const data: Record<string, string> = {};
          this.selectedModels.forEach((id, i) => (data[id] = this.rawOutputs[i] || ''));

          this.fire
            .callCombinedSummary(prompt, data)
            .subscribe({
              next: async (res) => {
                const summary = res?.summary?.trim() || '';
                const relevance = res?.relevance?.trim() || '';
                const furtherAnalysis = res?.furtherAnalysis?.trim() || res?.analysis?.trim() || '';
                const modelResultsMarkdown = this.buildModelResultsMarkdown();
                const hasSummaryContent = !!(summary || relevance || furtherAnalysis || modelResultsMarkdown.trim());

                this.summarySucceeded = hasSummaryContent;
                if (this.summarySucceeded) {
                  const structuredSummary = this.buildStructuredSummaryMarkdown(summary, relevance, furtherAnalysis);
                  this.teamSummary = structuredSummary;
                  this.summaryRelevance = relevance;
                  this.summaryFurtherAnalysis = furtherAnalysis;
                  this.formattedSummary = this.asHtml(structuredSummary);
                  this.summaryError = null;
                } else {
                  this.teamSummary = null;
                  this.summaryRelevance = null;
                  this.summaryFurtherAnalysis = null;
                  this.formattedSummary = '';
                  this.summaryError = 'Summary returned no content.';
                }

                this.isLoading = false;

                // only switch to Summary when summary succeeded
                if (this.summarySucceeded) {
                  const summaryTabIndex = 0;
                  this.selectedTabIndex = summaryTabIndex;
                  if (this.tabGroup) this.tabGroup.selectedIndex = summaryTabIndex;
                } else if (!firstSuccessfulTabShown) {
                  const firstFinishedIndex = this.tabStatuses.findIndex((s) => s !== 'idle');
                  if (firstFinishedIndex >= 0) {
                    const tabIndex = firstFinishedIndex + 1;
                    this.selectedTabIndex = tabIndex;
                    if (this.tabGroup) this.tabGroup.selectedIndex = tabIndex;
                  }
                }
                this.scheduleAnswerAutoFollow();

                await this.loadPrismIfNeeded();
                await this.loadHLJSIfNeeded();
                setTimeout(() => {
                  this.highlightAll();
                  this.highlightAllHLJS();
                }, 0);
              },
              error: (err) => {
                this.teamSummary = null;
                this.summaryRelevance = null;
                this.summaryFurtherAnalysis = null;
                this.formattedSummary = '';
                this.summarySucceeded = false;
                this.summaryError = `(Summary error) ${this.errString(err)}`;
                this.isLoading = false;
              }
            });
        }
      },
      error: (err) => {
        this.error = this.errString(err);
        this.isLoading = false;
      }
    });
  }

  // ✅ Updated: return Observables only (no Promises)
  private invokeModel(
    modelId: ModelId,
    prompt: string,
    history: Message[],
    length: AnswerLength = 'normal'
  ): Observable<string> {
    return defer(() =>
      from(this.invokeModelWithRetries(modelId, prompt, history, length))
    );
  }

  private async invokeModelWithRetries(
    modelId: ModelId,
    prompt: string,
    history: Message[],
    length: AnswerLength = 'normal'
  ): Promise<string> {
    const retryCandidates = this.fire.getAvailableRetryModels([modelId]) as ModelId[];
    const modelsToTry: ModelId[] = [modelId, ...retryCandidates.slice(0, this.maxRetries)];
    let lastError: unknown = null;

    for (const activeModel of modelsToTry) {
      try {
        const text = await firstValueFrom(this.invokeModelOnce(activeModel, prompt, history, length).pipe(take(1)));
        const normalized = String(text ?? '');
        if (normalized.trim()) return normalized;
        if (activeModel !== modelsToTry[modelsToTry.length - 1]) {
          throw new Error('Empty response');
        }
        return normalized;
      } catch (err) {
        lastError = err;
        this.fire.markRetryModelFailed(activeModel);
        if (activeModel !== modelsToTry[modelsToTry.length - 1]) {
          continue;
        }
        throw err;
      }
    }

    throw lastError ?? new Error('Model request failed.');
  }

  private invokeModelOnce(
    modelId: ModelId,
    prompt: string,
    history: Message[],
    length: AnswerLength = 'normal'
  ): Observable<string> {
    // NOTE: most service methods ignore extra params, but TS checks signatures.
    // We only call with args the methods actually accept in your Observable service.

    switch (modelId) {
      case 'chatgpt54':
        return this.fire.generateChatGPT54Text(prompt, history);

      case 'palmx':
        return this.fire.generatePalmXText(prompt, history);

      case 'gemini3flash':
      case 'gemini':
        return this.fire.generateGeminiText(prompt, history).pipe(map((t) => String(t ?? '')));

      case 'deepseek':
        return this.fire.generateDeepSeekText(prompt, history);

      case 'deepv3':
        return this.fire.generateDeepSeekV3Text(prompt, history);

      case 'glm47':
        return this.fire.generateZaiChatText(prompt, history);
      case 'qwen':
        return this.fire.generateQwenText(prompt, history);
      case 'glm5':
        return this.fire.getGLM5Chat(prompt, history);

      case 'novapro':
        return this.fire.generateNovaProText(prompt, history).pipe(map((r: any) => String(r?.text ?? '')));

      case 'maverick':
        return this.fire.generateMaverickText(prompt, history);

      case 'opus45':
        return this.fire.generateOpus45Text(prompt, history);

      case 'opus37':
        return this.fire.generateOpus37Text(prompt, history);

      case 'opus46':
      case 'opus6':
        return this.fire.generateOpyDopy6(prompt, history);

      case 'opus47':
        return this.fire.generateOpus47Text(prompt, history);

      case 'kimik25':
        return this.fire.generateKimiK25Text(prompt, history);

      case 'haiku45':
      case 'haiku':
        return this.fire.generateHaiku45Text(prompt, history);

      case 'sonnet45':
        return this.fire.generateSonnet45Text(prompt, history);

      case 'cohere':
        return this.fire.generateCohereText(prompt, history).pipe(
          map((resp: unknown) => this.parseCohereResponse(resp))
        );

      case 'commandLight':
        return this.fire.generateCommandLightText(prompt, history);

      case 'jamba':
        return this.fire.generateJambaText(prompt, history);

      case 'mistrallarge':
        return this.fire.generateMistralLargeText(prompt, history);

      case 'codestralto':
        return this.fire.generateCodestraltoText(prompt, history);

      case 'pixtralimage':
        return this.fire.generatePixtralImageText(prompt, history).pipe(map((r: any) => String(r?.text ?? '')));

      case 'scout':
        return this.fire.generateScoutText(prompt, history, length).pipe(map((t) => String(t ?? '')));

      case 'chatgpt5':
        return this.fire.generateChatGPT54Text(prompt, history);

      case 'minichatgpt54':
        return this.fire.generateMiniChatGPT54Text(prompt, history);

      case 'commandRInstruct':
        return this.fire.generateCohereText(prompt, history).pipe(
          map((resp: unknown) => this.parseCohereResponse(resp))
        );

      case 'llama31Instruct':
        return this.fire.generateMaverickText(prompt, history);

      case 'novalite':
        return this.fire.generateScoutText(prompt, history, length).pipe(map((t) => String(t ?? '')));

      default:
        void length;
        return of(`(Unknown model "${modelId}")`);
    }
  }

  // ---------------- UI helpers ----------------
  modelName(id: ModelId): string {
    return this.MODEL_CATALOG[id] || id;
  }

  tabLabel(id: ModelId): string {
    const shortNames: Record<string, string> = {
      chatgpt54: 'ChatGPT 5.4',
      chatgpt5: 'GPT 5.2',
      minichatgpt54: 'ChatGPT Mini',
      palmx: 'Writer X5',
      gemini3flash: 'Gemini',
      deepseek: 'DeepSeek R1',
      deepv3: 'DeepSeek V3.2',
      glm47: 'GLM',
      qwen: 'Qwen',
      glm5: 'GLM 5',
      novapro: 'Nova',
      maverick: 'Llama 4',
      opus45: 'Opus',
      opus37: 'Claude Opus 3.7',
      opus46: 'Opus 4.6',
      opus6: 'Opus 4.6',
      haiku45: 'Haiku 4.5',
      haiku: 'Haiku 4.5',
      sonnet45: 'Sonnet',
      cohere: 'Cohere',
      commandLight: 'Cmd Light',
      gemini: 'Gemini',
      jamba: 'Jamba',
      mistrallarge: 'Mistral Large',
      codestralto: 'Codestral',
      pixtralimage: 'Pixtral',
      scout: 'Scout',
      commandRInstruct: 'Cmd R',
      llama31Instruct: 'L3.1',
      novalite: 'Nova Lt'
    };
    return shortNames[id] || this.MODEL_CATALOG[id] || id;
  }

  getVendorLogo(modelId: ModelId): string | null {
    if (modelId === 'palmx') return 'assets/logos/x5-baby.png';
    const vendor = this.MODEL_VENDOR_MAP[modelId];
    return vendor ? `assets/logos/${vendor}.svg` : null;
  }

  hasVendorLogo(modelId: ModelId): boolean {
    return !!this.MODEL_VENDOR_MAP[modelId];
  }

  private buildStructuredSummaryMarkdown(
    summary: string | null,
    relevance: string | null,
    furtherAnalysis: string | null
  ): string {
    const sections: string[] = [];

    if (summary) {
      sections.push(`## Executive Summary\n\n${summary}`);
    }

    if (relevance) {
      sections.push(`## Why it matters\n\n${relevance}`);
    }

    if (furtherAnalysis) {
      sections.push(`## Further analysis\n\n${furtherAnalysis}`);
    }

    const modelResults = this.buildModelResultsMarkdown();
    if (modelResults.trim()) {
      sections.push(`## Model-by-model results\n\n${modelResults}`);
    }

    return sections.join('\n\n') || 'Summary returned no content.';
  }

  private buildModelResultsMarkdown(): string {
    const modelSections = this.selectedModels
      .map((modelId, index) => {
        const rawText = (this.rawOutputs[index] || '').trim();
        if (!rawText) {
          return '';
        }

        return `### ${this.modelName(modelId)}\n\n${rawText}`;
      })
      .filter(Boolean);

    return modelSections.join('\n\n');
  }

  private asHtml(markdown: string | null): SafeHtml {
    const aggressive = aggressivelyFormatMarkdownForCodeBlocks((markdown || '').toString());
    return this.sanitizer.bypassSecurityTrustHtml(this.md.makeHtml(aggressive));
  }

  private applyLiveTranscriptChunk(chunk: LiveTranscriptChunk): void {
    if (!chunk?.text) return;
    const role = chunk.role === 'model' ? 'model' : 'user';

    if (chunk.isFinal) {
      this.liveTranscriptSegments.push({ role, text: chunk.text });
      this.liveTranscriptPartial = null;
    } else {
      this.liveTranscriptPartial = { role, text: chunk.text };
    }

    this.liveTranscriptHtml = this.asHtml(this.buildLiveTranscriptMarkdown());
    this.cdr.detectChanges();
  }

  private buildLiveTranscriptMarkdown(): string {
    const lines = this.liveTranscriptSegments.map((segment) => this.formatLiveTranscriptLine(segment.role, segment.text));
    if (this.liveTranscriptPartial) {
      lines.push(this.formatLiveTranscriptLine(this.liveTranscriptPartial.role, this.liveTranscriptPartial.text, true));
    }
    return lines.join('\n\n');
  }

  private formatLiveTranscriptLine(
    role: 'user' | 'model',
    text: string,
    isLive = false
  ): string {
    const label = role === 'user' ? 'You' : 'Gemini';
    const suffix = isLive ? ' _(live)_': '';
    return `**${label}${suffix}:** ${text}`;
  }

  private parseCohereResponse(response: unknown): string {
    if (response == null) return '';
    if (typeof response === 'string') {
      const trimmed = response.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const parsed = JSON.parse(trimmed);
          return this.pickCohereText(parsed) ?? trimmed;
        } catch {
          return trimmed;
        }
      }
      return trimmed;
    }
    if (typeof response === 'object') {
      return this.pickCohereText(response) ?? String(response);
    }
    return String(response);
  }

  private pickCohereText(payload: any): string | null {
    if (!payload) return null;
    if (typeof payload.text === 'string') return payload.text;
    if (payload.model_output && typeof payload.model_output.text === 'string') {
      return payload.model_output.text;
    }
    if (Array.isArray(payload.history)) {
      const assistant = [...payload.history].reverse().find((entry: any) => entry?.role === 'assistant');
      if (assistant && typeof assistant.content === 'string') return assistant.content;
    }
    return null;
  }

  private errString(err: any): string {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    if (err.message) return err.message;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Error';
    }
  }

  scrollToTop(): void {
    this.getActiveAnswersScroller()?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private getActiveAnswersScroller(): HTMLElement | null {
    return this.tabGroupElement?.nativeElement.querySelector<HTMLElement>(
      '.mat-tab-body-active .mat-tab-body-content, .mat-mdc-tab-body-active .mat-mdc-tab-body-content'
    ) ?? null;
  }

  private isNearBottom(element: HTMLElement): boolean {
    return element.scrollHeight - element.clientHeight - element.scrollTop <= 96;
  }

  private scheduleAnswerAutoFollow(): void {
    if (!this.shouldAutoFollowAnswers) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.shouldAutoFollowAnswers) return;
        const scroller = this.getActiveAnswersScroller();
        if (!scroller || scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop <= 8) return;
        scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
      });
    });
  }

  autoResizeTextarea(preferredScrollTop?: number): void {
    const el = this.textareaRef?.nativeElement;
    if (!el) return;

    const MAX = 240;

    if (!el.value || el.value.trim().length === 0) {
      el.style.height = '';
      el.style.overflowY = 'hidden';
      el.scrollTop = 0;
      return;
    }

    const prevScrollTop = preferredScrollTop ?? el.scrollTop;
    const cs = window.getComputedStyle(el);
    const lineH = parseFloat(cs.lineHeight || '20') || 20;

    el.style.height = '';

    const scrollH = el.scrollHeight;
    const clientH = el.clientHeight || lineH;

    const fillRatio = scrollH / clientH;
    const shouldPreGrow = fillRatio >= 0.85;

    const desired = Math.min(scrollH + (shouldPreGrow ? lineH : 0), MAX);
    el.style.height = `${desired}px`;

    const capped = desired >= MAX;
    el.style.overflowY = capped ? 'auto' : 'hidden';

    if (capped) {
      if (this.composingAtTop) el.scrollTop = prevScrollTop;
      else el.scrollTop = el.scrollHeight;
    }
  }

  recallPreviousMessage(): void {
    if (this.messageHistory.length === 0) return;
    if (this.historyIndex === -1) this.historyIndex = this.messageHistory.length - 1;
    else if (this.historyIndex > 0) this.historyIndex--;
    this.inputText = this.messageHistory[this.historyIndex];
    this.focusTextarea();
  }

  recallNextMessage(): void {
    if (this.messageHistory.length === 0 || this.historyIndex === -1) return;
    if (this.historyIndex < this.messageHistory.length - 1) {
      this.historyIndex++;
      this.inputText = this.messageHistory[this.historyIndex];
    } else {
      this.historyIndex = -1;
      this.inputText = '';
    }
    this.focusTextarea();
  }

  get activeModelId(): ModelId | null {
    const tabIndex = this.selectedTabIndex ?? 0;
    
    const modelIndex = tabIndex - 1;
    
    return modelIndex >= 0 && modelIndex < this.selectedModels.length
    ? this.selectedModels[modelIndex]
    : null;
    }

  get showModelNameReasoningDropdown(): boolean {
    return !!this.activeModelId && this.reasoningService.modelSupportsReasoning(this.activeModelId);
  }

  getReasoningLabel(level: ReasoningLevel): string {
    const labels = {
      auto: 'Auto',
      instant: 'Instant',
      thinking: 'Thinking',
      pro: 'Pro'
    };
    return labels[level] || 'Thinking';
  }

  getReasoningIndicatorArc(): string {
    if (this.selectedReasoningLevel === 'instant' || this.selectedReasoningLevel === 'auto') {
      return '90deg';
    }
    if (this.selectedReasoningLevel === 'thinking') {
      return '180deg';
    }
    return '270deg';
  }

  modelReasoning(modelId: ModelId): string {
    return 'Tap to see the model background and capability highlights.';
  }

 modelLogo(modelId: ModelId): string {
  if (modelId === 'palmx') return 'assets/logos/x5-baby.png';
  const vendor = this.MODEL_VENDOR_MAP[modelId];
  return vendor ? `assets/logos/${vendor}.svg` : 'assets/logos/iamclari2.png';
}


  private openDialog(component: any, panelClass: string = 'Sonnet4-dialog-panel'): void {
    this.dialog.open(component, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass
    });
  }

  openModelSpecDialog(modelId: ModelId): void {
    this.clickedModels.add(modelId);

    const dialogMap: Partial<Record<ModelId, () => void>> = {
      chatgpt54: () => this.openDialog(GptFiveComponent),
      gemini: () => this.openDialog(GeminiProComponent), 
      gemini3flash: () => this.openDialog(GeminiProComponent),
      deepseek: () => this.openDialog(DeepseekComponent),
      deepv3: () => this.openDialog(Deepseekv3Component),
      glm47: () => this.openDialog(GlmComponent),
      glm5: () => this.openDialog(GlmComponent),
      opus45: () => this.openDialog(OpusFourComponent),
      opus37: () => this.openDialog(OpusFourComponent),
      opus46: () => this.openDialog(OpusSixComponent),
      opus6: () => this.openDialog(OpusSixComponent),
      opus47: () => this.openDialog(OpusSixComponent),

      haiku45: () => this.openDialog(HaikuComponent),
      haiku: () => this.openDialog(HaikuComponent),
      sonnet45: () => this.openDialog(SonnetFourComponent),
      pixtralimage: () => this.openDialog(PixtralImageComponent),
      mistrallarge: () => this.openDialog(MistralLargeComponent),
      maverick: () => this.openDialog(MaverickComponent),
      jamba: () => this.openDialog(JambaComponent),
      palmx: () => this.openDialog(PalmyraComponent),
      cohere: () => this.openDialog(CohereRPlusComponent),
      commandLight: () => this.openDialog(CohereRPlusComponent),
      novapro: () => this.openDialog(NovaProComponent),
      scout: () => this.openDialog(ScoutComponent),
      chatgpt5: () => this.openDialog(GptFiveComponent),
      minichatgpt54: () => this.openDialog(GptFiveComponent),
      codestralto: () => this.openDialog(MistralLargeComponent),
      commandRInstruct: () => this.openDialog(CohereRPlusComponent),
      llama31Instruct: () => this.openDialog(MaverickComponent),
      novalite: () => this.openDialog(ScoutComponent)
    };

    const open = dialogMap[modelId];
    if (open) open();
  }

  // ──────────────────────────────────────────────
  // Placeholder rotation helpers (stable, bug-free)
  // ──────────────────────────────────────────────
  private clearPlaceholderTimer(): void {
    if (this.placeholderTimer) {
      clearTimeout(this.placeholderTimer);
      this.placeholderTimer = null;
    }
  }

  private clearShimmerTimer(): void {
    if (this.shimmerTimer) {
      clearTimeout(this.shimmerTimer);
      this.shimmerTimer = null;
    }
  }

  private schedulePlaceholder(token: number, delayMs: number, fn: () => void): void {
    this.clearPlaceholderTimer();
    this.ngZone.runOutsideAngular(() => {
      this.placeholderTimer = setTimeout(() => {
        if (this.destroyed || token !== this.cycleToken) return;
        this.ngZone.run(() => fn());
      }, delayMs);
    });
  }

  private scheduleShimmer(token: number, delayMs: number, fn: () => void): void {
    this.clearShimmerTimer();
    this.ngZone.runOutsideAngular(() => {
      this.shimmerTimer = setTimeout(() => {
        if (this.destroyed || token !== this.cycleToken) return;
        this.ngZone.run(() => fn());
      }, delayMs);
    });
  }

  private startPerfectPlaceholderCycle(): void {
  this.destroyed = false;
  this.cycleToken++;

  this.headerTypingIndex = 0;
  this.headerCharIndex = 0;
  this.headerDeleting = false;

  this.placeholderIndex = 0;
  this.currentOption = this.placeholderOptions[this.placeholderIndex];

  // start empty, type forward
  this.currentPlaceholder = '';

  this.logoFading = false;
  this.modelNameFading = false;

  this.stopShimmer();
  this.startShimmer(this.cycleToken);

  this.cdr.detectChanges();

  this.stepHeaderTypewriter(this.cycleToken);
}

private stepHeaderTypewriter(token: number): void {
  if (this.destroyed || token !== this.cycleToken) return;

  const option = this.placeholderOptions[this.headerTypingIndex];
  const fullText = option?.text ?? '';

  // ✅ force logo DOM re-create when logoUrl changes (restarts CSS animation)
if (this.currentOption?.logoUrl !== option?.logoUrl) {
  this.showLogo = false;
  this.cdr.detectChanges();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      this.logoKey++;
      this.currentOption = option;
      this.showLogo = true;

      // also reset shimmer letters for the new name immediately
      this.stopShimmer();
      this.shimmerDirection = 'teal-to-pink';
      this.startShimmer(token);

      this.cdr.detectChanges();
    });
  });
} else {
  this.currentOption = option;
}

  // Keep model name/logo in sync with the phrase we’re typing
  this.placeholderIndex = this.headerTypingIndex;

  if (!this.headerDeleting) {
    // typing forward
    this.headerCharIndex = Math.min(this.headerCharIndex + 1, fullText.length);
    this.currentPlaceholder = fullText.slice(0, this.headerCharIndex);
    this.cdr.detectChanges();

    if (this.headerCharIndex >= fullText.length) {
      // pause then delete
      this.headerDeleting = true;
      this.scheduleTyping(token, this.pauseAfterTypeMs, () => this.stepHeaderTypewriter(token));
    } else {
      this.scheduleTyping(token, this.typeSpeedMs, () => this.stepHeaderTypewriter(token));
    }
    return;
  }

  // deleting backward
  this.headerCharIndex = Math.max(this.headerCharIndex - 1, 0);
  this.currentPlaceholder = fullText.slice(0, this.headerCharIndex);
  this.cdr.detectChanges();

  if (this.headerCharIndex <= 0) {
    // move to next phrase, pause, then type forward
    this.headerDeleting = false;
    this.headerTypingIndex = (this.headerTypingIndex + 1) % this.placeholderOptions.length;

    // optional: restart shimmer for the next model name
    this.stopShimmer();
    this.shimmerDirection = 'teal-to-pink';
    this.startShimmer(token);

    this.scheduleTyping(token, this.pauseAfterDeleteMs, () => this.stepHeaderTypewriter(token));
  } else {
    this.scheduleTyping(token, this.backspaceSpeedMs, () => this.stepHeaderTypewriter(token));
  }
}


  private stopPerfectPlaceholderCycle(): void {
  this.destroyed = true;
  this.cycleToken++;
  this.clearPlaceholderTimer();
  this.clearShimmerTimer();
  this.clearTypingTimer();
  this.stopShimmer();
}


  private transitionToNextOption(token: number, immediate = false): void {
    if (this.destroyed || token !== this.cycleToken) return;

    const prevIndex = this.placeholderIndex;
    const nextIndex = (prevIndex + 1) % this.placeholderOptions.length;

    this.logoFading = true;
    this.modelNameFading = true;
    this.stopShimmer();
    this.cdr.detectChanges();

    const fadeMs = immediate ? 60 : this.fadeDurationMs;

    this.schedulePlaceholder(token, fadeMs, () => {
      this.placeholderIndex = nextIndex;
      this.currentOption = this.placeholderOptions[this.placeholderIndex];
      this.currentPlaceholder = this.currentOption.text || '';

      this.logoFading = false;
      this.modelNameFading = false;

      this.shimmerDirection = 'teal-to-pink';
      this.startShimmer(token);

      this.cdr.detectChanges();

      this.schedulePlaceholder(token, this.placeholderHoldMs, () =>
        this.transitionToNextOption(token)
      );
    });
  }

  private startShimmer(token: number): void {
    this.stopShimmer();
    this.isShimmering = true;

    this.shimmerLetters = Array.from(this.currentOption?.name || 'AI Model');
    this.shimmerLetterIndex = -1;

    this.stepShimmer(token);
  }

  private stepShimmer(token: number): void {
    if (this.destroyed || token !== this.cycleToken || !this.isShimmering) return;

    this.shimmerLetterIndex++;

    if (this.shimmerLetterIndex >= this.shimmerLetters.length) {
      this.stopShimmer();
      this.cdr.detectChanges();
      return;
    }

    this.cdr.detectChanges();
    this.scheduleShimmer(token, this.shimmerStepMs, () => this.stepShimmer(token));
  }

  private stopShimmer(): void {
    this.clearShimmerTimer();
    this.isShimmering = false;
    this.shimmerLetterIndex = -1;
  }

  // ---------------- model placeholders ----------------
  private resetModelPlaceholderTimers(clearOnly = false): void {
    Object.keys(this.modelPlaceholderTimers).forEach((key) => {
      const t = this.modelPlaceholderTimers[Number(key)];
      if (t) clearTimeout(t);
    });
    this.modelPlaceholderTimers = {};

    if (clearOnly) return;

    this.selectedModels.forEach((modelId, index) => {
      this.modelPlaceholders[index] = '';
      this.modelPlaceholderIndices[index] = 0;
      setTimeout(() => this.startModelPlaceholderCycle(index, modelId), 1200 + index * 300);
    });
  }

  private resetTabStatuses(): void {
    this.tabStatuses = this.selectedModels.map(() => 'idle');
  }

  private createEmptyRawOutputs(): (string | null)[] {
    return this.selectedModels.map(() => null);
  }

  private createEmptyFormattedOutputs(): SafeHtml[] {
    return this.selectedModels.map(() => '');
  }

  private createIdleTabStatuses(): TabStatus[] {
    return this.selectedModels.map(() => 'idle');
  }

  startModelPlaceholderCycle(tabIndex: number, modelId: ModelId): void {
    if (this.rawOutputs[tabIndex]) return;

    const options = this.modelPlaceholderOptions[modelId] || ['Waiting for response...'];
    const currentIndex = this.modelPlaceholderIndices[tabIndex] || 0;
    const nextIndex = (currentIndex + 1) % options.length;

    this.modelPlaceholders[tabIndex] = options[currentIndex];
    this.modelPlaceholderIndices[tabIndex] = nextIndex;
    this.cdr.detectChanges();

    this.modelPlaceholderTimers[tabIndex] = setTimeout(() => {
      this.startModelPlaceholderCycle(tabIndex, modelId);
    }, this.modelPlaceholderHoldMs);
  }

  getModelPlaceholder(tabIndex: number): string {
    return this.modelPlaceholders[tabIndex] || '';
  }

  // ---------------- code highlighting ----------------
  private async loadPrismIfNeeded(): Promise<void> {
    if (this.prismLoaded) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const core = await import('prismjs');
    await Promise.all([
      import('prismjs/components/prism-markup'),
      import('prismjs/components/prism-markdown'),
      import('prismjs/components/prism-javascript'),
      import('prismjs/components/prism-typescript'),
      import('prismjs/components/prism-json'),
      import('prismjs/components/prism-css'),
      import('prismjs/components/prism-scss'),
      import('prismjs/components/prism-bash'),
      import('prismjs/components/prism-shell-session'),
      import('prismjs/components/prism-python'),
      import('prismjs/components/prism-java'),
      import('prismjs/components/prism-csharp'),
      import('prismjs/components/prism-cpp'),
      import('prismjs/components/prism-yaml'),
      import('prismjs/components/prism-sql')
    ]).catch(() => {});

    this.prism = (window as any).Prism || (core as any);
    this.prismLoaded = !!this.prism;
  }

  private async loadHLJSIfNeeded(): Promise<void> {
    if (this.hljsLoaded) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const mod = await import('highlight.js/lib/common');
    this.hljs = (mod as any).default || (mod as any);
    this.hljsLoaded = !!this.hljs;
  }

  private highlightAll(): void {
    try {
      if (!this.prismLoaded || !this.prism) return;
      const root = this.tabGroupElement?.nativeElement || document;
      if ((this.prism as any).highlightAllUnder) (this.prism as any).highlightAllUnder(root);
      else if ((this.prism as any).highlightAll) (this.prism as any).highlightAll();
    } catch {}
  }

  private highlightAllHLJS(): void {
    try {
      if (!this.hljsLoaded || !this.hljs) return;
      const root: HTMLElement = this.tabGroupElement?.nativeElement || document.body;
      const blocks = root.querySelectorAll('pre code:not([class*="language-"])');

      blocks.forEach((codeEl: Element) => {
        const pre = codeEl.parentElement as HTMLElement | null;
        if ((codeEl as HTMLElement).querySelector('.token') || (codeEl as HTMLElement).classList.contains('prism')) return;
        this.hljs.highlightElement(codeEl as HTMLElement);
        pre?.classList.add('hljs');
      });
    } catch {}
  }

  // ---------------- export ----------------
  exportToPDF(): void {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 40;
    const width = doc.internal.pageSize.getWidth() - margin * 2;
    let y = margin;

    const add = (title: string, text: string | null) => {
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(title, margin, y);
      y += 18;

      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(12);

      const cleaned = this.stripMarkdownForExport((text || '').toString());
      const lines = doc.splitTextToSize(cleaned, width);

      for (const line of lines) {
        if (y > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 16;
      }
      y += 8;
    };

    add('Team Summary', this.teamSummary);
    this.selectedModels.forEach((id, i) => add(this.modelName(id), this.rawOutputs[i] || ''));
    doc.save('team-ai-summary.pdf');
  }

  exportToDOCX(): void {
    const section = (title: string, body: string | null): docx.Paragraph[] => {
      const out: docx.Paragraph[] = [
        new docx.Paragraph({ text: title, heading: docx.HeadingLevel.HEADING_2, spacing: { after: 240 } })
      ];

      const cleaned = this.stripMarkdownForExport(body || '');
      cleaned.split('\n').forEach((line) => out.push(new docx.Paragraph({ text: line.trim(), spacing: { after: 120 } })));
      return out;
    };

    const children: docx.Paragraph[] = [...section('Team Summary', this.teamSummary)];
    this.selectedModels.forEach((id, i) => children.push(...section(this.modelName(id), this.rawOutputs[i] || '')));

    const d = new docx.Document({ sections: [{ properties: {}, children }] });
    docx.Packer.toBlob(d).then((blob) => saveAs(blob, 'team-ai-summary.docx'));
  }

  private stripMarkdownForExport(md: string): string {
    let s = (md || '').toString();
    s = s.replace(/^(\s{0,3})#{1,6}\s+/gm, '$1');
    s = s.replace(/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/gm, '');
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
    s = s.replace(/\*\*\*([^*]+)\*\*\*/g, '$1');
    s = s.replace(/\*\*([^*]+)\*\*/g, '$1');
    s = s.replace(/\*([^*]+)\*/g, '$1');
    s = s.replace(/`([^`]+)`/g, '$1');
    s = s.replace(/\n{3,}/g, '\n\n');
    return s.trim();
  }

private clearTypingTimer(): void {
  if (this.typingTimer) {
    clearTimeout(this.typingTimer);
    this.typingTimer = null;
  }
}

private scheduleTyping(token: number, delayMs: number, fn: () => void): void {
  this.clearTypingTimer();
  this.ngZone.runOutsideAngular(() => {
    this.typingTimer = setTimeout(() => {
      if (this.destroyed || token !== this.cycleToken) return;
      this.ngZone.run(() => fn());
    }, delayMs);
  });
}





}
